import React, { useEffect } from "react"
import styled from "styled-components"
import compose from "recompose/compose"
import { withRouter } from "react-router-dom"
import { withApollo } from "@apollo/react-hoc"
import FileSaver from "file-saver"
import { differenceInDays } from "@/helpers/dates"
import { withDateRange } from "@/helpers/hoc/withDateRange"
import { withi18n } from "@/helpers/hoc/withi18n"
import { withTracking } from "@/helpers/hoc/withTracking"
import { Button } from "@xingternal/winery-ui/lib/Button"
import { Loading } from "@xingternal/winery-ui/lib/Loading"
import ColleaguesReportQuery from "./ColleaguesReportQuery.gql"
import { useNotifications } from "@/components/Notifications"

const i18n = {
  exportButtonText: "360_EXPORT_BUTTON_TITLE",
  exportingButtonText: "360_EXPORT_BUTTON_LOADING",
  exportError: "360_RECRUITER_INSIGHTS_ERROR_MESSAGE_DEFAULT",
}

const getDaysBucket = (days) => {
  switch (true) {
    case days < 8:
      return "1_7"
    case days < 15:
      return "8_14"
    case days < 32:
      return "15_31"
    case days < 93:
      return "32_92"
    case days < 201:
      return "93_200"
    case days < 367:
      return "201_366"
    default:
      return "367_+"
  }
}

const StyledButton = styled(Button)`
  position: relative;
`

StyledButton.defaultProps = {
  variant: "tertiary",
  icon: "CoreDownload",
}

const LoadingExport = styled(Loading)`
  position: absolute;
  background: white;
  top: 0;
  left: 10px;
  margin: 7px 2px;
`

LoadingExport.defaultProps = {
  size: 16,
}

const ExportButton = ({
  days,
  exportButtonText,
  exportingButtonText,
  loading,
  onClick,
  trackAction,
  ...props
}) => {
  const triggerDownload = () => {
    trackAction("xri_export_report_button_click", {
      PropContextDimension2: "xri_export_colleagues",
      PropContextDimension4: `timeframe_${getDaysBucket(days)}`,
    })
    onClick()
  }

  return (
    <StyledButton disabled={loading} onClick={triggerDownload} {...props}>
      {loading ? (
        <>
          <LoadingExport />
          {` ${exportingButtonText}`}
        </>
      ) : (
        exportButtonText
      )}
    </StyledButton>
  )
}

const enhanceExportButton = compose(withTracking(), withi18n(i18n))

const EnhancedExportButton = enhanceExportButton(ExportButton)

const base64ToBlob = (data, contentType) => {
  const raw = window.atob(data)
  const rawLength = raw.length
  const uInt8Array = new Uint8Array(rawLength)

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i)
  }

  const blob = new Blob([uInt8Array], {
    type: contentType,
  })

  return blob
}

const useReportDownload = ({ from, to, client }) => {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)

  const triggerDownload = async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, errors } = await client.query({
        query: ColleaguesReportQuery,
        variables: { from, to },
      })

      setLoading(false)
      setError(errors)

      if (errors) return

      const {
        colleaguesExport: { zipFile, zipName },
      } = data

      FileSaver.saveAs(base64ToBlob(zipFile, "application/zip"), zipName)
    } catch (error) {
      setLoading(false)
      setError(error)
    }
  }

  return { error, loading, triggerDownload }
}

const ExportReportButton = ({
  className,
  client,
  match: { path },
  from,
  to,
  exportError,
}) => {
  const { loading, triggerDownload, error } = useReportDownload({
    from,
    to,
    client,
  })
  const { showNotification } = useNotifications()
  const inColleagues = path === "/colleagues"
  const days = differenceInDays(to, from)

  useEffect(() => {
    if (error) {
      showNotification({ message: exportError, notificationType: "fail" })
    }
  }, [error, exportError, showNotification])

  return inColleagues ? (
    <EnhancedExportButton
      className={className}
      onClick={triggerDownload}
      loading={loading}
      days={days}
    />
  ) : null
}

const enhanced = compose(withi18n(i18n), withRouter, withApollo, withDateRange)

const EnhancedExportReportButton = enhanced(ExportReportButton)

export default EnhancedExportReportButton
